import React from "react"
import ReactDOM from "react-dom/client"
import "./input.css"
import App from "./App"
import { AuthProvider } from "./context/authProvider"
import { UiProvider } from "./context/uiProvider"
import { DataProvider } from "./context/dataProvider"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { ErrorBoundary } from "react-error-boundary"
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import ErrorFallback from "./pages/errors/ErrorFallback"
import { PublicSiteProvider } from "./contexts/PublicSiteContext"
import { SelectedCompanyProvider } from "./context/companyProvider"
import { getDocumentTitle, getPublicSiteId, redirectToCanonicalHostIfNeeded } from "./utils/publicSite"
import { getPublicSiteBranding } from "./content/publicSiteBranding"

redirectToCanonicalHostIfNeeded()

const siteId = getPublicSiteId()
const branding = getPublicSiteBranding(siteId)

if (branding.htmlThemeClass) {
  document.documentElement.classList.add(branding.htmlThemeClass)
}

document.title = getDocumentTitle()

const theme = document.querySelector('meta[name="theme-color"]')
if (theme) {
  const themeColors: Record<string, string> = {
    'security-walk': '#0C447C',
    'construction-walk': '#2F5A3C',
    'default': '#8e44ad',
  }
  theme.setAttribute("content", themeColors[siteId] || themeColors.default)
}

const description = document.querySelector('meta[name="description"]')
if (description) {
  description.setAttribute("content", branding.metaDescription)
}

const iconLink = document.querySelector('link[rel="icon"]') as HTMLLinkElement | null
if (iconLink) {
  const faviconPaths: Record<string, string> = {
    'security-walk': '/images/patrolwalk-icon.png',
    'construction-walk': '/images/constructionwalk-icon.svg',
  }
  const faviconPath = faviconPaths[siteId]
  if (faviconPath) {
    iconLink.href = `${process.env.PUBLIC_URL || ""}${faviconPath}`
  }
}

const manifestLink = document.querySelector('link[rel="manifest"]') as HTMLLinkElement | null
if (manifestLink) {
  const manifestPaths: Record<string, string> = {
    'security-walk': '/manifest-patrolwalk.json',
    'construction-walk': '/manifest-constructionwalk.json',
  }
  const manifestPath = manifestPaths[siteId]
  if (manifestPath) {
    manifestLink.href = `${process.env.PUBLIC_URL || ""}${manifestPath}`
  }
}

const appleTouchIcon = document.querySelector('link[rel="apple-touch-icon"]') as HTMLLinkElement | null
if (appleTouchIcon) {
  const appleTouchIconPaths: Record<string, string> = {
    'security-walk': '/patrolwalk-favicon-192.png',
    'construction-walk': '/images/constructionwalk-icon.svg',
  }
  const appleTouchIconPath = appleTouchIconPaths[siteId]
  if (appleTouchIconPath) {
    appleTouchIcon.href = `${process.env.PUBLIC_URL || ""}${appleTouchIconPath}`
  }
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      suspense: true,
      useErrorBoundary: true,
    },
  },
})

const root = ReactDOM.createRoot(document.getElementById("root") as HTMLElement)
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <ErrorBoundary FallbackComponent={ErrorFallback}>
        <AuthProvider>
          <UiProvider>
            <DataProvider>
              <PublicSiteProvider>
                <SelectedCompanyProvider>
                  <App />
                </SelectedCompanyProvider>
              </PublicSiteProvider>
            </DataProvider>
          </UiProvider>
        </AuthProvider>
      </ErrorBoundary>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  </React.StrictMode>
)
